import type { HttpService } from '../../../App/services/http-service';
import type { UserProfileService } from '../../../App/services/user-profile-service';
import type { ApplicationProvider, MxApplication } from '../../../App/model/application';
import type { UserProfile } from '../../../App/model/user-profile';
import type { DeviceService } from '../../../App/services/device-service';

interface ApplicationDTO {
	Name: string;
	Title: string;
	Id: string;

	Icon?: string;
	NavigationItem?: string;
	Position?: number;
	ShowFavorites?: boolean;
	ShowRecent?: boolean;
	SimpleNavigation?: boolean;
	ShowNotificationMonitor?: boolean;
	Url?: string;
	HelpValue?: string;
	HelpSourceType?: number;
	UnifiedSearchEnabled?: boolean;
	UnifiedSearchTitle1?: string;
	UnifiedSearchTitle2?: string;
	UnifiedSearchBackground?: string;
	UnifiedSearchTitleColorOverride?: string;
}

/**
 * @ngdoc service
 * @kind function
 * @name mx.shell.ApplicationProviderService
 * @module mx.shell
 *
 * @description
 * Represents an interface for injection concrete implementation of the Application Provider service.
 * The Application has to provide the concrete implementation of this service, which is registered in the di.map.json file.
 */
export class ApplicationProviderService implements ApplicationProvider {
	private loadApplicationPromise?: ReturnType<ApplicationProviderService['loadApplication']>;
	static $inject = ['mx.SolutionBuilderAgent.Http', 'mx.shell.UserProfileService', 'mx.app.DeviceService'];
	constructor(private readonly http: HttpService, private readonly userProfileService: UserProfileService, private readonly deviceService: DeviceService) {}

	/**
	 * @ngdoc method
	 * @name mx.shell.ApplicationProviderService#load
	 *
	 * @description
	 * Loads application items.
	 *
	 * @returns {promise} Resolved as an array of MxApplication
	 */
	public async load(reload = false) {
		if (!this.loadApplicationPromise || reload) {
			this.loadApplicationPromise = this.loadApplication();
		}

		return await this.loadApplicationPromise;
	}

	private mapAppItem(item: ApplicationDTO, profile: UserProfile): MxApplication {
		return {
			icon: item.Icon,
			name: item.Name,
			navigationItem: item.NavigationItem,
			position: item.Position,
			showFavourites: item.ShowFavorites,
			showRecent: item.ShowRecent,
			simpleNavigation: Boolean(item.SimpleNavigation) && !this.deviceService.screenMobile(),
			showOperationMonitor: item.ShowNotificationMonitor,
			title: item.Title,
			url: item.Url,
			value: item.Id,
			helpValue: item.HelpValue,
			helpSourceType: item.HelpSourceType,
			unifiedSearchEnabled: Boolean(item.UnifiedSearchEnabled),
			unifiedSearchTitle1: item.UnifiedSearchTitle1?.replace('{UserFirstName}', profile.firstName || ''),
			unifiedSearchTitle2: item.UnifiedSearchTitle2,
			unifiedSearchBackground: item.UnifiedSearchBackground,
			unifiedSearchTitleColorOverride: item.UnifiedSearchTitleColorOverride,
		};
	}

	private async loadApplication() {
		const profile = await this.userProfileService.get();
		const applications = (await this.http.get<ApplicationDTO[]>('api/Application')) || [];
		return applications.map((app) => this.mapAppItem(app, profile));
	}
}
